import { Modal, Text, TextArea } from '@viapeople/components'
import type React from 'react'
import { useRef, useState } from 'react'
import { type ExportedForm, parseImportedForm } from '../helpers/formExportImport'

interface ImportFormDialogProps {
	isOpen: boolean
	onClose: () => void
	onImport: (formData: ExportedForm) => void
}

export const ImportFormDialog: React.FC<ImportFormDialogProps> = ({ isOpen, onClose, onImport }) => {
	const [jsonInput, setJsonInput] = useState('')
	const [error, setError] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleImport = () => {
		if (!jsonInput.trim()) {
			setError('Please paste JSON or upload a file')
			return
		}

		const result = parseImportedForm(jsonInput)

		if (!result.success) {
			setError(result.error)
			return
		}

		onImport(result.data)
		handleClose()
	}

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		const reader = new FileReader()
		reader.onload = (e) => {
			const content = e.target?.result as string
			setJsonInput(content)
			setError(null)
		}
		reader.onerror = () => {
			setError('Failed to read file')
		}
		reader.readAsText(file)
	}

	const handleClose = () => {
		setJsonInput('')
		setError(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title='Import Form'
			buttons={[
				{
					children: 'Cancel',
					variant: 'secondary',
					onClick: handleClose,
				},
				{
					children: 'Import Form',
					variant: 'primary',
					onClick: handleImport,
					disabled: !jsonInput.trim(),
				},
			]}>
			<Text variant='small' className='mb-4'>
				Paste the exported form JSON below or upload a JSON file:
			</Text>

			<div className='mb-4'>
				<input
					ref={fileInputRef}
					type='file'
					accept='.json,application/json'
					onChange={handleFileUpload}
					className='block w-full text-sm text-neutral-500
            file:mr-4 file:py-2 file:px-4
            file:rounded file:border-0
            file:text-sm file:font-semibold
            file:bg-primary-50 file:text-primary-700
            hover:file:bg-primary-100
            cursor-pointer'
				/>
			</div>

			<TextArea
				value={jsonInput}
				onChange={(e) => {
					setJsonInput(e.target.value)
					setError(null)
				}}
				placeholder='Paste form JSON here or upload a file above...'
				className='font-mono text-sm mb-2'
				rows={20}
			/>

			{error && (
				<Text variant='small' className='text-error-600'>
					{error}
				</Text>
			)}
		</Modal>
	)
}
